
import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import { naraShops as initialShops } from '../mocks/prefectures';
import type { Shop } from '../mocks/prefectures';

const LS_KEY = 'admin_shops_data';

function loadFromStorage(): Shop[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed;
      }
    }
  } catch {
    // ignore
  }
  return initialShops;
}

function saveToStorage(data: Shop[]) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(data));
  } catch {
    // ignore
  }
}

export interface ExtendedShop extends Shop {
  attributeId?: string;
  mainGenreId?: string;
  mainPartId?: string;
}

interface ShopStoreContextType {
  shops: Shop[];
  addShop: (shop: Shop) => void;
  updateShop: (id: number, data: Partial<Shop>) => void;
  deleteShop: (id: number) => void;
  setShops: (shops: Shop[]) => void;
}

const ShopStoreContext = createContext<ShopStoreContextType | undefined>(undefined);

export function ShopStoreProvider({ children }: { children: ReactNode }) {
  const [shops, setShopsState] = useState<Shop[]>(loadFromStorage);

  useEffect(() => {
    saveToStorage(shops);
  }, [shops]);

  const setShops = useCallback((newShops: Shop[]) => {
    setShopsState(newShops);
  }, []);

  const addShop = useCallback((shop: Shop) => {
    setShopsState((prev) => [...prev, shop]);
  }, []);

  const updateShop = useCallback((id: number, data: Partial<Shop>) => {
    setShopsState((prev) =>
      prev.map((s) => (s.id === id ? { ...s, ...data } as Shop : s))
    );
  }, []);

  const deleteShop = useCallback((id: number) => {
    setShopsState((prev) => prev.filter((s) => s.id !== id));
  }, []);

  return (
    <ShopStoreContext.Provider
      value={{ shops, addShop, updateShop, deleteShop, setShops }}
    >
      {children}
    </ShopStoreContext.Provider>
  );
}

export function useShopStore(): ShopStoreContextType {
  const ctx = useContext(ShopStoreContext);
  if (ctx === undefined) {
    throw new Error('useShopStore must be used within a ShopStoreProvider');
  }
  return ctx;
}
